import React, { useState, useContext, useMemo, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { buildListRoute, parseRouteEntityId } from "../../utils";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import { usePermissions } from "../../hooks/usePermissions";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import DeviceProfilesCardView from "./DeviceProfilesCardView";
import DeviceProfilesListingView from "./DeviceProfilesListingView";
import DeviceProfileSidebar from "./DeviceProfileSidebar";
import AddDeviceProfileWizard from "./AddDeviceProfileWizard";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { softDeleteData, restoreData } from "../../utils/softDelete";
import "../../starek.css";

const UI_STRINGS = {
    PAGE_TITLE: "Device Profiles",
    ENTITY_SINGULAR: "Device Profile",
    ENTITY_PLURAL: "Device Profiles",
    DELETE_TITLE_SINGLE: (name) => `Delete Device Profile ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Device Profiles?`,
    DELETE_MESSAGE_SINGLE: (
        name,
    ) => `You are about to delete device profile "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (
        count,
    ) => `You are about to delete ${count} device profile${count !== 1 ? "s" : ""}.

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total device profiles selected: ${count}`,
};

const GET_DEVICE_PROFILES = gql`
    query GetDeviceProfiles($where: DeviceProfileWhereInput!) {
        deviceProfiles(where: $where) {
            id
            name
            description
            configuration
            plant_id
            is_active
            deleted_at
            created_at
            updated_at
            created_by {
                id
                name
            }
            updated_by {
                id
                name
            }
        }
    }
`;

const UPDATE_MANY_DEVICE_PROFILE = gql`
    mutation UpdateManyDeviceProfile($data: DeviceProfileUpdateManyMutationInput!, $where: DeviceProfileWhereInput) {
        updateManyDeviceProfile(data: $data, where: $where) {
            count
        }
    }
`;

const CREATE_ONE_DEVICE_PROFILE = gql`
    mutation CreateOneDeviceProfile($data: DeviceProfileCreateInput!) {
        createOneDeviceProfile(data: $data) {
            id
            name
            description
            configuration
        }
    }
`;

const UPDATE_ONE_DEVICE_PROFILE = gql`
    mutation UpdateOneDeviceProfile(
        $data: DeviceProfileUpdateInput!
        $where: DeviceProfileWhereUniqueInput!
    ) {
        updateOneDeviceProfile(data: $data, where: $where) {
            id
            name
            description
            configuration
        }
    }
`;

const DELETE_ONE_DEVICE_PROFILE = gql`
    mutation DeleteOneDeviceProfile($id: Int!) {
        deleteOneDeviceProfile(where: { id: $id }) {
            id
        }
    }
`;

const DeviceProfiles = () => {
    const context = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeProfileId = parseRouteEntityId(routeId);
    const { can } = usePermissions();
    const canWrite   = can("device_profile", "WRITE");
    const canDestroy = can("device_profile", "DESTROY");
    const [searchParams, setSearchParams] = useSearchParams();

    // View state lives in the URL so a refresh, a deep link or a back-navigation
    // lands on exactly the same list the user was looking at.
    const showInactive = searchParams.get("showInactive") === "1";

    const patchSearchParams = (patch, options = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                next.delete(key);
            } else {
                next.set(key, value);
            }
        });
        setSearchParams(next, options);
    };

    const setShowInactive = (value, options = {}) =>
        patchSearchParams({ showInactive: value ? "1" : null }, options);

    const deviceProfileQueryWhere = {
        plant_id: { equals: context?.lastUsedPlant?.id },
        ...(showInactive ? {} : { is_active: { equals: true } }),
    };

    const { data, loading, refetch } = useQuery(GET_DEVICE_PROFILES, {
        variables: { where: deviceProfileQueryWhere },
        skip: !context?.lastUsedPlant?.id,
        client: context.engineClient,
    });

    const [createDeviceProfile] = useMutation(CREATE_ONE_DEVICE_PROFILE, {
        client: context.engineClient,
    });
    const [updateDeviceProfile] = useMutation(UPDATE_ONE_DEVICE_PROFILE, {
        client: context.engineClient,
    });
    const [deleteOneDeviceProfile] = useMutation(DELETE_ONE_DEVICE_PROFILE, {
        client: context.engineClient,
    });
    const [updateManyDeviceProfile] = useMutation(UPDATE_MANY_DEVICE_PROFILE, { client: context.engineClient });
    const [togglingActiveId, setTogglingActiveId] = useState(null);

    const [showSidebar, setShowSidebar] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [viewMode, setViewMode] = useState("card");
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    // Tracks the route id we already widened the query for, so the retry runs at most once.
    const inactiveRetryRef = useRef(null);
    const [routeEditProfile, setRouteEditProfile] = useState(null);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "card" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [toggleCleared, setToggleCleared] = useState(false);

    const deviceProfiles = data?.deviceProfiles ?? [];

    useEffect(() => {
        if (!routeId) {
            setRouteEditProfile(null);
            return;
        }

        if (loading) return;

        const listRoute = buildListRoute("/device-profiles", searchParams);

        if (routeProfileId == null) {
            context.showError("Device profile not found");
            setRouteEditProfile(null);
            navigate(listRoute, { replace: true });
            return;
        }

        const profileToEdit = deviceProfiles.find(
            (p) => Number(p?.id) === routeProfileId,
        );

        if (!profileToEdit) {
            if (!showInactive && inactiveRetryRef.current !== routeProfileId) {
                inactiveRetryRef.current = routeProfileId;
                const widened = new URLSearchParams(searchParams);
                widened.set("showInactive", "1");
                setSearchParams(widened, { replace: true });
                return;
            }
            context.showError("Device profile not found");
            setRouteEditProfile(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditProfile(profileToEdit);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeProfileId
                ? current
                : [profileToEdit],
        );
        setShowSidebar(true);
    }, [
        routeId,
        routeProfileId,
        loading,
        deviceProfiles,
        showInactive,
        searchParams,
        setSearchParams,
        navigate,
        context,
    ]);

    const filteredProfiles = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return deviceProfiles;
        return deviceProfiles.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [deviceProfiles, filterText]);

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleRowClicked = (row) => {
        navigate(buildListRoute(`/device-profiles/${row.id}`, searchParams));
    };

    const handleAdd = () => {
        setSelectedRows([]);
        setShowSidebar(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        navigate(buildListRoute(`/device-profiles/${selectedRows[0].id}`, searchParams));
    };

    const handleSidebarClose = async () => {
        setShowSidebar(false);
        setRouteEditProfile(null);
        setSelectedRows([]);
        setToggleCleared(!toggleCleared);
        if (routeId) {
            navigate(buildListRoute("/device-profiles", searchParams), {
                replace: true,
            });
        }
        await refetch();
    };

    const handleSaveDeviceProfile = async (profilePayload) => {
        try {
            const currentUserId =
                context?.currentUser?.id ||
                context?.user?.id ||
                context?.me?.id ||
                2;

            if (!profilePayload.id) {
                // Create
                await createDeviceProfile({
                    variables: {
                        data: {
                            name: profilePayload.name,
                            description: profilePayload.description || "",
                            configuration: profilePayload.configuration || {},
                            plant: {
                                connect: { id: context.lastUsedPlant.id },
                            },
                            created_by: {
                                connect: { id: Number(currentUserId) },
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            } else {
                // Update
                await updateDeviceProfile({
                    variables: {
                        where: { id: profilePayload.id },
                        data: {
                            name: { set: profilePayload.name },
                            description: {
                                set: profilePayload.description || "",
                            },
                            configuration: profilePayload.configuration,
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            }

            await handleSidebarClose();
            context.showSuccess(
                profilePayload.id
                    ? `Device Profile "${profilePayload.name}" updated successfully`
                    : `Device Profile "${profilePayload.name}" created successfully`,
            );
        } catch (error) {
            context.showError(error);
            throw error;
        }
    };

    const handleDeleteDeviceProfile = async (profile) => {
        try {
            await deleteOneDeviceProfile({
                variables: { id: profile.id },
            });
            await handleSidebarClose();
            context.showSuccess(
                `Device Profile "${profile.name}" deleted successfully`,
            );
        } catch (error) {
            context.showError(error);
            throw error;
        }
    };

    const handleDelete = (profiles) => {
        if (!profiles || profiles.length === 0 || isDeleting) return;

        const isSingle = profiles.length === 1;
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(profiles[0].name)
            : UI_STRINGS.DELETE_TITLE_BULK(profiles.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(profiles[0].name)
            : UI_STRINGS.DELETE_MESSAGE_BULK(profiles.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(profiles.length)}
                        &nbsp;
                        <span className="badge bg-info">{profiles.length}</span>
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);

                try {
                    for (const profile of profiles) {
                        try {
                            await deleteOneDeviceProfile({
                                variables: { id: profile.id },
                            });
                        } catch (err) {
                            context.showError(err);
                        } finally {
                            setDeleteDone((d) => d + 1);
                        }
                    }
                    await refetch();
                    setSelectedRows([]);
                    setToggleCleared(!toggleCleared);
                } finally {
                    setIsDeleting(false);
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    const handleUpdateConfiguration = async (profileId, configuration) => {
        try {
            await updateDeviceProfile({
                variables: {
                    where: { id: profileId },
                    data: {
                        configuration: configuration,
                    },
                },
            });
            await refetch();
        } catch (error) {
            context.showError(error);
            throw error;
        }
    };

    const handleToggleActive = async (profiles, makeActive) => {
        const userId = context?.currentUser?.id ?? context?.user?.id;
        const ids = profiles.map((p) => p.id);
        try {
            await updateManyDeviceProfile({
                variables: {
                    data: makeActive ? restoreData() : softDeleteData(),
                    where: { id: { in: ids } },
                },
            });
            context.showSuccess(makeActive ? `Restored ${ids.length} profile(s)` : `Deactivated ${ids.length} profile(s)`);
            setSelectedRows([]);
            setToggleCleared(!toggleCleared);
            await refetch();
        } catch (err) {
            context.showError(err);
        }
    };

    const handleToggleActiveSingle = async (profile) => {
        const userId = context?.currentUser?.id ?? context?.user?.id;
        setTogglingActiveId(profile.id);
        try {
            const deactivating = profile.is_active;
            await updateManyDeviceProfile({
                variables: {
                    data: deactivating ? softDeleteData() : restoreData(),
                    where: { id: { in: [profile.id] } },
                },
            });
            context.showSuccess(
                deactivating
                    ? `${profile.name} is deactivated`
                    : `${profile.name} is restored`,
            );
            await refetch();
        } catch (err) {
            context.showError(err);
        } finally {
            setTogglingActiveId(null);
        }
    };

    const profileExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every((r) => r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
        },
    ] : [];

    if (loading) return <EntityLoadingState icon="fa-sd-card" />;

    return (
        <div className="container-fluid">
            {deviceProfiles && (
                <>
                    <div className="font-weight-bold mb-3">
                        {UI_STRINGS.PAGE_TITLE}
                    </div>

                    <EntityOperationBar
                        entity={{
                            singular: UI_STRINGS.ENTITY_SINGULAR,
                            plural: UI_STRINGS.ENTITY_PLURAL,
                        }}
                        allowSearch={true}
                        allowAdd={canWrite}
                        allowDelete={canDestroy}
                        allowEdit={canWrite}
                        allowExtendedActions={canWrite && selectedRows.length > 0}
                        extendedActions={profileExtendedActions}
                        allowShowInactive={true}
                        showInactive={showInactive}
                        onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); }}
                        defaultView={viewMode}
                        selectedRows={selectedRows}
                        isDeleting={isDeleting}
                        deleteDone={deleteDone}
                        onSearch={(text) => setFilterText(text)}
                        onAdd={handleAdd}
                        onViewChange={(mode) => {
                            setViewMode(mode);
                            patchSearchParams({ view: mode });
                            setSelectedRows([]);
                        }}
                        onEdit={handleEdit}
                        onDelete={() => handleDelete(selectedRows)}
                    >
                        <EntityOperationBar.View
                            mode="card"
                            icon="fa fa-th"
                            tooltip="Card View"
                        >
                            <DeviceProfilesCardView
                                profiles={filteredProfiles}
                                selectedRows={selectedRows}
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                toggleCleared={toggleCleared}
                                onEdit={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowSidebar(true);
                                }}
                                onConfigure={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowWizard(true);
                                }}
                                onAdd={handleAdd}
                                onRowClicked={handleRowClicked}
                                onToggleActive={handleToggleActiveSingle}
                                togglingActiveId={togglingActiveId}
                            />
                        </EntityOperationBar.View>

                        <EntityOperationBar.View
                            mode="list"
                            icon="fa fa-list"
                            tooltip="List View"
                        >
                            <DeviceProfilesListingView
                                profiles={filteredProfiles}
                                selectedRows={selectedRows}
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                toggleCleared={toggleCleared}
                                onEdit={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowSidebar(true);
                                }}
                                onConfigure={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowWizard(true);
                                }}
                                noDataComponent={<EntityEmptyState icon="fa-sd-card" entityName="Device Profile" messages={EMPTY_MESSAGES.deviceProfiles} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                                onRowClicked={handleRowClicked}
                            />
                        </EntityOperationBar.View>
                    </EntityOperationBar>
                </>
            )}

            <DeviceProfileSidebar
                show={showSidebar}
                onDiscard={handleSidebarClose}
                item={routeEditProfile || selectedRows[0] || null}
                plantId={context.lastUsedPlant.id}
                onSave={handleSaveDeviceProfile}
                onDelete={handleDeleteDeviceProfile}
                onToggleActive={handleToggleActiveSingle}
            />

            {/* Wizard Modal */}
            {showWizard && (
                <AddDeviceProfileWizard
                    modal={true}
                    profile={selectedRows[0] || null}
                    initialConfig={selectedRows[0]?.configuration}
                    onClose={() => {
                        setShowWizard(false);
                    }}
                    onFinish={async (manifest) => {
                        // Update the device profile configuration
                        if (selectedRows[0]?.id) {
                            await handleUpdateConfiguration(
                                selectedRows[0].id,
                                manifest,
                            );
                        }
                        setShowWizard(false);
                    }}
                />
            )}

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                count={selectedRows.length}
                done={deleteDone}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { DeviceProfiles };
